import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine, LabelList, Cell } from 'recharts';

const PdfHorizontalBarChart = ({ 
  data = [], 
  xAxisLabel = 'X Axis', 
  yAxisLabel = 'Y Axis', 
  colors = ['#003C73', '#76C8E8', '#B0EAC0','#FBCF4E','#F6A4A4','#C9B0D5'],
  barSize = 16,
  height = 400,
  showReferenceLine = true,
  referenceLineValue = 50,
  domain = [0, 100],
  ticks = [0, 20, 40, 60, 80, 100],
  valueFormatter = (value) => value,
  showLegend = true
}) => {
  // Calculate dynamic height based on number of bars to fit all data without scrolling
  const calculatedHeight = Math.max(height, data.length * (barSize + 8) + 100);
  
  // Wrap long Y-axis tick labels onto multiple lines (max 3 words per line)
  const renderWrappedYAxisTick = (props) => {
    const { x = 0, y = 0, payload } = props || {};
    const text = payload?.value ?? '';
    if (!text) return null;
    const words = String(text).split(' ').filter(Boolean);
    const lines = [];
    const maxWordsPerLine = 3;
    for (let i = 0; i < words.length; i += maxWordsPerLine) {
      lines.push(words.slice(i, i + maxWordsPerLine).join(' '));
    }
    const lineHeight = 14;
    const totalHeight = lineHeight * (lines.length - 1);
    return (
      <text x={x - 5} y={y - totalHeight / 2} textAnchor="end" fill="#374151" fontSize="12">
        {lines.map((line, idx) => (
          <tspan key={idx} x={x - 5} dy={idx === 0 ? 0 : lineHeight}>{line}</tspan>
        ))}
      </text>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full flex items-center justify-center text-gray-500" style={{ height: `${height}px` }}>
        <div className="text-sm">No data available</div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-full">
      <div style={{ flex: 1, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 40,
            }}
          >
            <XAxis 
              type="number" 
              domain={domain}
              ticks={ticks}
              label={{ value: xAxisLabel, position: 'bottom', offset: 10 }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={200}
              tick={renderWrappedYAxisTick}
              tickMargin={8}
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0 }}
            />
            
            {showReferenceLine && (
              <ReferenceLine x={referenceLineValue} stroke="#666" strokeDasharray="3 3" />
            )}
            
            <Bar 
              dataKey="value" 
              barSize={barSize}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
              <LabelList 
                dataKey="value" 
                position="right" 
                formatter={valueFormatter}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Compact Legend - Only show if explicitly requested */}
      {showLegend && data.length > 0 && data.length <= 6 && (
        <div className="mt-2 px-2" style={{ minHeight: '30px' }}>
          <div className="flex flex-wrap gap-2 justify-center">
            {data.slice(0, 6).map((entry, index) => (
              <div key={index} className="flex items-center gap-1">
                <div 
                  className="w-2 h-2 rounded"
                  style={{ backgroundColor: colors[index % colors.length] }}
                ></div>
                <span className="text-xs truncate" style={{ maxWidth: '80px' }} title={entry.name}>
                  {entry.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfHorizontalBarChart;
